import { approvalsNotificationListMaxLimit } from '@gonfalon/dogfood-flags/dogfood-flags';
import { msg } from '@gonfalon/messages/msg';
import type { RefinementCtx } from 'zod';
import { z } from 'zod';

import { isValidResourceConfirmation } from '#utils/confirmationUtils';

// Zod schema for PendingChangesFormRecord
export const pendingChangesFormSchema = z.object({
  isScheduleSelected: z.boolean(),
  bypassRequiredApprovals: z.boolean(),

  executionDate: z.number(),
  reviewers: z.array(
    z.object({
      id: z.string(),
      type: z.enum(['member', 'team']),
    }),
  ),
  acknowledgeConflicts: z.boolean(),
  resourceConfirmation: z.string(),
  comment: z.string(),
  integrationConfig: z.record(z.string(), z.union([z.string(), z.boolean(), z.number(), z.undefined()])),
});

export const pendingChangesValidation = (
  data: PendingChangesFormSchema,
  ctx: RefinementCtx,
  resourceInfo: ResourceInfo,
  formRequirements: FormRequirements,
) => {
  if (data.isScheduleSelected) {
    // The execution date is a timestamp, not a date object, so these are reported as generic
    // issues. Nothing reads the issue code here; the form only renders the message on the field.
    if (data.executionDate <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['executionDate'],
        message: msg('pendingChanges.reviewAndSave.executionDateError'),
      });
    }

    if (data.executionDate <= new Date().getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['executionDate'],
        message: msg('pendingChanges.reviewAndSave.executionDateError'),
      });
    }
  }
  const isApprovalRequired = data.bypassRequiredApprovals ? false : formRequirements.isApprovalRequired;
  const isApprovalRequestedOrRequired = isApprovalRequired || data.reviewers.length > 0;

  // Validate reviewer count dynamically
  if (isApprovalRequired && formRequirements.usesLaunchDarklyApprovalService && data.reviewers.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      path: ['reviewers'],
      input: data.reviewers,
      minimum: formRequirements.minNumberOfApprovals,
      inclusive: true,
      origin: 'array',
      message: msg('pendingChanges.reviewAndSave.minReviewersError', { count: formRequirements.minNumberOfApprovals }),
    });
  }

  if (approvalsNotificationListMaxLimit() < data.reviewers.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_big,
      path: ['reviewers'],
      input: data.reviewers,
      maximum: approvalsNotificationListMaxLimit(),
      inclusive: false,
      origin: 'number',
      message: msg('pendingChanges.reviewAndSave.maxReviewersError', { max: approvalsNotificationListMaxLimit() }),
    });
  }
  const isCommentRequired = formRequirements.isCommentRequired || isApprovalRequestedOrRequired;

  if (isCommentRequired && data.comment.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      path: ['comment'],
      input: data.comment,
      minimum: 1,
      inclusive: true,
      origin: 'string',
      message: msg('pendingChanges.reviewAndSave.commentRequiredError'),
    });
  }
  if (data.comment.length > 5000) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_big,
      path: ['comment'],
      input: data.comment,
      maximum: 5000,
      inclusive: false,
      origin: 'string',
      message: msg('pendingChanges.reviewAndSave.commentTooLongError'),
    });
  }

  // If a reviewer is added, confirmation is not required
  if (formRequirements.isConfirmationRequired && !isApprovalRequestedOrRequired) {
    if (data.resourceConfirmation.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['resourceConfirmation'],
        message: msg('pendingChanges.reviewAndSave.confirmationRequiredError'),
      });
    }
    const isResourceConfirmationInvalid = isValidResourceConfirmation(
      resourceInfo.resourceKey,
      resourceInfo.resourceName,
    )('resourceConfirmation')({ resourceConfirmation: data.resourceConfirmation });

    if (isResourceConfirmationInvalid) {
      isResourceConfirmationInvalid.errors.forEach((error) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['resourceConfirmation'],
          message: error,
        });
      });
    }
  }

  if (formRequirements.isAcknowledgeConflictsRequired && !data.acknowledgeConflicts) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['acknowledgeConflicts'],
      message: msg('pendingChanges.reviewAndSave.acknowledgeConflictsError'),
    });
  }
};

export interface FormRequirements {
  usesLaunchDarklyApprovalService: boolean;
  isConfirmationRequired: boolean;
  isAcknowledgeConflictsRequired: boolean;
  isCommentRequired: boolean;
  isApprovalRequired: boolean;
  minNumberOfApprovals: number;
  canBypassRequiredApprovals: boolean;
}

export interface ResourceInfo {
  resourceKey: string;
  resourceName: string;
}

export const getPendingChangesFormSchema = (params: {
  formRequirements: FormRequirements;
  resourceInfo: ResourceInfo;
}) => {
  const { formRequirements, resourceInfo } = params;

  return pendingChangesFormSchema.superRefine((data, ctx) => {
    return pendingChangesValidation(data, ctx, resourceInfo, formRequirements);
  });
};

export type PendingChangesFormSchema = z.infer<typeof pendingChangesFormSchema>;
